import { type FC, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import type { SportsEventSearchResult } from "src/http/api-types";
import { useContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { STALE_TIME_ONE_HOUR } from "src/shared/utils/query/retry-constants";
import { EmptyCarousel } from "src/website-apps/shared/components/carousel";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { LocCarouselListSection } from "../LocCarouselListSection";
import { EventsNearYouCarousel } from "./EventsNearYouCarousel";
import { EventsNearYouList } from "./EventsNearYouList";

interface Props {
  categoryId: number | undefined;
}

/**
 * Carousel of events within a sports league grouping near the user's current preferred location
 */
export const EventsNearYou: FC<Props> = ({ categoryId }) => {
  const storePageEvent = useStorePageEvent();
  const [state] = useEventsGridContext();
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const content = useContent("cid$#3263598#"); // Events near
  const { dates, location } = state.filters;
  const createQueryFn = useCreateQueryFn();
  const { lt, lg } = location ?? {};
  const { from, to } = dates?.dates ?? {};

  /**
   * Retrieve Events Near You on Location Change
   */
  const {
    data: events = [],
    error,
    isLoading: pending,
  } = useQuery<Maybe<SportsEventSearchResult[]>>({
    queryKey: createQueryKey(QueryKeyPrefix.EVENTS_NEAR_YOU, { categoryId, lg, lt, from, to }),
    queryFn: createQueryFn({
      method: "GetSportsEventsNearYou",
      params: [
        ["categoryId", categoryId ?? ""],
        ["userLongitude", lg ?? ""],
        ["userLatitude", lt ?? ""],
        ["fromDate", typeof from === "string" ? from : (from?.toJSON() ?? "")],
        ...(to ? [["toDate", typeof to === "string" ? to : to.toJSON()] as const] : []),
      ],
    }),
    enabled: Boolean(lg && lt && from),
    staleTime: STALE_TIME_ONE_HOUR, // 1 hour
  });

  /**
   * Page event handler for teams near you card click
   */
  const onCardClick = useCallback(
    (id: number | undefined) => {
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.CATEGORY_GROUPING_EVENTS_NEAR_YOU_CARD_CLICK, String(id));
    },
    [storePageEvent],
  );

  return (
    <LocCarouselListSection headerContent={content} placement="GroupingEventsNearYou">
      {!error &&
        !pending &&
        (isMobile ? (
          <EventsNearYouList events={events ?? []} onClick={onCardClick} />
        ) : (
          <EventsNearYouCarousel id="EventsNearYou" events={events ?? []} onClick={onCardClick} />
        ))}

      {pending && <EmptyCarousel noResult={false} placeHolderLineCount={1} size={isMobile ? 2 : 3} />}
    </LocCarouselListSection>
  );
};
